import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  REMOVED,
  TestSetupModule,
  outputParams
} from '@nrwl/test-setup';
import { isNil, cloneDeep, findIndex } from 'lodash';
import { FormsModule, NgForm } from '@angular/forms';
import {
  CONFIRM_SELECTION,
  BertConfigFieldMapping as ConfigFieldMapping,
  FRAME_LOSS_TYPE,
  LAYER_TYPES,
  TRANSMIT_INTERFACE_TYPE,
  VLAN_CONFIG_TYPE,
  SVLAN_ETHER_TYPE,
  CVLAN_ETHER_TYPE,
  DEFINED_ADDRESS_TYPES,
  TRANSMIT_INTERFACE_VALUE,
  TX_IMAGE,
  IPAddressValue,
  SUBNET_MAP,
  IP_ADDRESS_TYPE,
  definedAddressValue,
  REVERSE_CONFIRM_SELECTION,
  SOURCE_ADDRESS_FROM,
  sourceAddressFrom,
  LoopbackTypes,
  vlanConfigValue
} from '../../models/model';
import { CommonMaterialModule } from '@nrwl/common-material';
import {
  CommonRegEx,
  Debounce,
  DevicesService,
  IPHelper,
  TestMode,
  UnmanagedDevicesService
} from '@nrwl/common-lib';
import { BertSetupService } from '../../bert-setup.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'nrwl-bert-endpoints',
  standalone: true,
  imports: [CommonModule, CommonMaterialModule, FormsModule, TestSetupModule],
  templateUrl: './bert-endpoints.component.html',
  styleUrls: ['./bert-endpoints.component.scss']
})
export class BertEndpointsComponent implements OnInit, OnDestroy {
  @Input() tab: BaseSetupTab;
  @Input() setupCtrl: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreValues?: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreAgentBasedConfig?: Function;
  @Input() previousOutput: outputParams;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('currentForm', { static: true }) form: NgForm;
  validIPV4RegEx: RegExp = CommonRegEx.IP_REGEX;
  validIPRegex: RegExp;
  subnetIPV4RegEx = CommonRegEx.SUBNET_MASK_REGEX;
  subnetRegEx: RegExp;
  macRegex = CommonRegEx.MAC;
  useArpTypes = CONFIRM_SELECTION;
  config: any;
  outputs: Array<string>;
  configMapping = ConfigFieldMapping;
  testMode = TestMode.LOOPBACK;
  vlanConfigTypes = VLAN_CONFIG_TYPE;
  etherTypesData = [];
  cVlanEtherTypeData = CVLAN_ETHER_TYPE;
  deiEnableData = CONFIRM_SELECTION;
  verifyVlanData = CONFIRM_SELECTION;
  frameLossIntervalTypes = FRAME_LOSS_TYPE;
  suppressLoopTypes = REVERSE_CONFIRM_SELECTION;
  definedAddressTypes = DEFINED_ADDRESS_TYPES;
  destDefinedAddressTypes = cloneDeep(DEFINED_ADDRESS_TYPES);
  sourceAddressFrom = SOURCE_ADDRESS_FROM;
  swapMacTypes = CONFIRM_SELECTION;
  primingTypes = CONFIRM_SELECTION;
  layerTypes = LAYER_TYPES;
  layer = LAYER_TYPES[0].value;
  ipAddressTypes = IP_ADDRESS_TYPE;
  ipType = IPAddressValue.IP_V4;
  ipErrorMsg: string;
  editMode = false;
  newTestMode = true;
  cloneMode = false;
  dhcpEnabled = false;
  //eslint-disable-next-line
  selectedOriginator: any;
  selectedDestination: any;
  txTypeImage = TX_IMAGE.EQUIPMENT;
  txInterfaceTypes = TRANSMIT_INTERFACE_TYPE;
  isJmepAsLb = false;
  ipFields = {
    srcAssignedAddress: null,
    srcTestIpAddress: null,
    srcSubnet: null,
    srcGateway: null,
    destAssignedAddress: null,
    destTestIpAddress: null,
    destManualAddress: null
  };
  destTxTypeImage = TX_IMAGE.EQUIPMENT;
  formField = {
    txInterface: null,
    definedAddress: null,
    sourceAddressFrom: null,
    srcMacAddress: null,
    srcEquipmentMac: null,
    srcNetworkMac: null,
    srcIpAddress: null,
    srcIpV4Address: null,
    srcSubnet: null,
    srcGateway: null,
    srcEditLayer2IP: true,
    srcEditLayer3IP: true,
    srcEditLayer2MAC: true,
    srcEditLayer3MAC: true,
    srcEditLayer3Subnet: true,
    useArp: null,
    destDefinedAddress: null,
    destTxInterface: null,
    destEquipmentMac: null,
    destNetworkMac: null,
    destMacAddress: null,
    destIpAddress: null,
    destIpV4Address: null,
    destShowIpLayer2: false,
    destShowMacLayer3: false,
    vlanConfig: null,
    svlan: null,
    etherType: null,
    cvlan: null,
    cVlanEtherType: null,
    deiEnable: null,
    remoteVlan: null,
    vlanPriority: null,
    frameLossInterval: null,
    suppressLoop: null,
    swapMac: null,
    sendPriming: null
  };

  showOrginatorDetails = false;
  showDestinationDetails = false;
  agentType: string;
  layer3Enabled = false;
  layer2Enabled = true;
  tabsubscription: Subscription;
  onAgentChanged = false;
  selectedLoopbackTypes: string;
  badIpValues = ['', '0.0.0.0', '255.255.255.255', '0', '::/0'];
  formTooltip = {
    srcMacAddress: 'Selected transmit interface determines the factory default MAC address',
    srcIpAddress: 'Management IP address that is assigned to JMEP'
  };
  constructor(
    public setupService: BertSetupService,
    private readonly deviceService: DevicesService,
    private readonly unmanagedDevicesService: UnmanagedDevicesService
  ) {}

  ngOnInit(): void {
    this.tab.form = this.form;
    if (this.setupService.tabDataChangeEvents) {
      this.tabsubscription = this.setupService.tabDataChangeEvents.subscribe(event => {
        if (event === 1) {
          this.restoreFormValues({ restoreAgentConfig: false });
        }
      });
    }
    this.restoreFormValues({ restoreAgentConfig: true });
  }

  ngOnDestroy(): void {
    if (this.tabsubscription) {
      this.tabsubscription.unsubscribe();
    }
  }

  restoreOtherTabValues(): void {
    this.ipType = this.restoreFieldValues(ConfigFieldMapping.IP_ADDRESS_TYPE);
    this.layer = this.restoreFieldValues(ConfigFieldMapping.LAYER_TO_TEST);
    this.testMode = this.restoreFieldValues(ConfigFieldMapping.TEST_MODE);
  }

  //eslint-disable-next-line
  restoreFormValues(paramConfig): void {
    this.dhcpEnabled = false;
    this.newTestMode = this.setupService.mode === 'new';
    this.editMode = this.setupService.editMode;
    this.cloneMode = this.setupService.mode === 'clone';
    this.tab.form = this.form;
    this.restoreOtherTabValues();
    this.config = this.tab.config;
    this.outputs = this.config.def.outputs;
    this.selectedOriginator = this.restoreFieldValues(ConfigFieldMapping.SELECTED_ORIGINATOR);
    this.selectedDestination = this.restoreFieldValues(ConfigFieldMapping.SELECTED_DESTINATION);
    this.etherTypesData = this.config.options[ConfigFieldMapping.ETHER_TYPE].value;
    this.formField.txInterface = this.restoreFieldValues(ConfigFieldMapping.TRANSMIT_INTERFACE);
    this.formField.definedAddress = this.restoreFieldValues(ConfigFieldMapping.DEFINED_ADDRESS);
    this.formField.sourceAddressFrom = this.restoreFieldValues(
      ConfigFieldMapping.SOURCE_ADDRESS_FROM
    );
    this.formField.srcMacAddress = this.restoreFieldValues(ConfigFieldMapping.SRC_MAC_ADDRESS);
    this.formField.srcEquipmentMac = this.restoreFieldValues(ConfigFieldMapping.SRC_EQUIPMENT_MAC);
    this.formField.srcNetworkMac = this.restoreFieldValues(ConfigFieldMapping.SRC_NETWORK_MAC);

    this.ipFields.srcAssignedAddress = this.restoreFieldValues(
      ConfigFieldMapping.SRC_ASSIGNED_IP_ADDRESS
    );
    this.ipFields.srcTestIpAddress = this.restoreFieldValues(
      ConfigFieldMapping.SRC_TEST_IP_ADDRESS
    );
    this.formField.srcIpV4Address = this.restoreFieldValues(ConfigFieldMapping.SRC_IPV4_ADDRESS);
    if (this.formField.srcIpV4Address === sourceAddressFrom.DHCP) {
      this.formField.sourceAddressFrom = JSON.parse(JSON.stringify(sourceAddressFrom.DHCP));
    }
    this.formField.srcSubnet = this.restoreFieldValues(ConfigFieldMapping.SRC_SUBNET);
    this.formField.srcGateway = this.restoreFieldValues(ConfigFieldMapping.SRC_GATEWAY);
    this.formField.srcEditLayer2IP = this.restoreFieldValues(
      ConfigFieldMapping.SRC_EDIT_LAYER_2_IP
    );
    this.formField.srcEditLayer3IP = this.restoreFieldValues(
      ConfigFieldMapping.SRC_EDIT_LAYER_3_IP
    );
    this.formField.srcEditLayer2MAC = this.restoreFieldValues(
      ConfigFieldMapping.SRC_EDIT_LAYER_2_MAC
    );
    this.formField.srcEditLayer3MAC = this.restoreFieldValues(
      ConfigFieldMapping.SRC_EDIT_LAYER_3_MAC
    );
    this.formField.useArp = this.restoreFieldValues(ConfigFieldMapping.USE_ARP);
    this.formField.destTxInterface = this.restoreFieldValues(
      ConfigFieldMapping.DEST_TRANSMIT_INTERFACE
    );
    this.formField.destDefinedAddress = this.restoreFieldValues(
      ConfigFieldMapping.DEST_DEFINED_ADDRESS
    );
    this.formField.destIpV4Address = this.restoreFieldValues(ConfigFieldMapping.DEST_IPV4_ADDRESS);
    this.formField.destMacAddress = this.restoreFieldValues(ConfigFieldMapping.DESTINATION_MAC);
    this.formField.destShowIpLayer2 = this.restoreFieldValues(
      ConfigFieldMapping.DEST_SHOW_IP_LAYER2
    );
    this.formField.destShowMacLayer3 = this.restoreFieldValues(
      ConfigFieldMapping.DEST_SHOW_MAC_LAYER3
    );
    this.formField.vlanConfig = this.restoreFieldValues(ConfigFieldMapping.VLAN_CONFIG);
    this.formField.svlan = this.restoreFieldValues(ConfigFieldMapping.SVLAN_ID);
    this.formField.etherType = this.restoreFieldValues(ConfigFieldMapping.ETHER_TYPE);
    this.formField.cvlan = this.restoreFieldValues(ConfigFieldMapping.CVLAN_ID);
    this.formField.cVlanEtherType = this.restoreFieldValues(ConfigFieldMapping.CVLAN_ETHER_TYPE);
    this.formField.deiEnable = this.restoreFieldValues(ConfigFieldMapping.DEI_ENABLE);
    this.formField.remoteVlan = this.restoreFieldValues(ConfigFieldMapping.REMOTE_VLAN_TAG);
    this.formField.vlanPriority = this.restoreFieldValues(ConfigFieldMapping.VERIFY_PRIORITY);
    this.formField.frameLossInterval = this.restoreFieldValues(
      ConfigFieldMapping.FRAME_LOSS_INTERVAL
    );
    this.formField.suppressLoop = this.restoreFieldValues(
      ConfigFieldMapping.SUPPRESS_LOOP_COMMANDS
    );
    this.formField.swapMac = this.restoreFieldValues(ConfigFieldMapping.SWAP_MAC_ADDRESS);
    this.formField.sendPriming = this.restoreFieldValues(ConfigFieldMapping.SEND_PRIMING_PACKETS);
    if (paramConfig.restoreAgentConfig) {
      this.getDataBasedOnAgentConfig();
      this.checkIPAddressType();
      this.setManagedLoopbackDeviceValues();
      this.onAgentChanged = false;
    }

    this.checkLayerType();
    this.onSourceAddressFromChange();
    if (this.showFormField(ConfigFieldMapping.USE_ARP)) {
      this.onArpChange();
    }
    if (this.showFormField(ConfigFieldMapping.DEFINED_ADDRESS)) {
      //JMEP when no test ip defined or if it IPV6 hide testIp option
      if (
        !this.ipFields.srcTestIpAddress ||
        this.ipFields.srcTestIpAddress === '0.0.0.0' ||
        IPHelper.isIPv6(this.ipFields.srcTestIpAddress)
      ) {
        if (this.formField.definedAddress === definedAddressValue.TEST_IP) {
          this.formField.definedAddress = definedAddressValue.MANAGEMENT_IP;
        }
        this.definedAddressTypes[0].enabled = false;
      } else {
        this.definedAddressTypes[0].enabled = true;
      }
      this.onDefinedAddressChange();
      this.onTxInterfaceChange();
    }
    this.selecteUnmanagedLoopbackTypes();
    this.onVlanConfigChange();
  }

  checkIPAddressType(): void {
    if (this.onAgentChanged) {
      this.formField.srcGateway = this.ipFields.srcGateway;
      this.formField.srcSubnet = this.ipFields.srcSubnet;
      this.formField.srcIpAddress = this.ipFields.srcTestIpAddress;
    } else {
      this.formField.srcIpAddress = this.formField.srcIpV4Address || this.ipFields.srcTestIpAddress;
    }
    this.formField.destIpAddress = this.formField.destIpV4Address;
    this.validIPRegex = this.validIPV4RegEx;
    this.subnetRegEx = this.subnetIPV4RegEx;
    this.ipErrorMsg = 'Address is not a valid IPv4 address';
    this.onSourceIpAddressChange();
    this.onDestinationIpAddressChange();
  }

  showFormField(field: string): boolean {
    return this.outputs.includes(field);
  }

  //eslint-disable-next-line
  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }
  // eslint-disable-next-line
  onAgentChange(data: any): void {
    this.onAgentChanged = true;
    this.selectedOriginator = this.deviceService.findDeviceByID(data.originatorId);
    this.selectedDestination = this.deviceService.findDeviceByID(data.destinationId);
    if (this.selectedOriginator) {
      this.onDataEmit(ConfigFieldMapping.ENGINE_A_INFO, data['engine-a-info'], true);
      this.onDataEmit(ConfigFieldMapping.SELECTED_ORIGINATOR, this.selectedOriginator, true);
    }
    if (this.selectedDestination) {
      this.onDataEmit(
        ConfigFieldMapping.SELECTED_DESTINATION,
        this.selectedDestination,
        data.isValid
      );
      this.onDataEmit(ConfigFieldMapping.ENGINE_B_INFO, data['engine-b-info'], data.isValid);
    }
    this.agentType = this.selectedOriginator?.getModel() || this.selectedDestination?.getModel();
    this.onDataEmit(ConfigFieldMapping.AGENT_TYPE, this.agentType, true);
    this.restoreAgentBasedConfig(this.agentType, this.testMode);
    if (data.destinationId && data['engine-b-info']['agent-type'] === 'unmanaged') {
      this.isJmepAsLb = false;
      this.setUnManagedDeviceValues(data.destinationId);
    }
    if (
      data.destinationId &&
      this.testMode == TestMode.LOOPBACK &&
      data['engine-b-info']['agent-type'] !== 'unmanaged'
    ) {
      this.setManagedLoopbackDeviceValues();
    }
    this.restoreFormValues({ restoreAgentConfig: true });
  }

  setManagedLoopbackDeviceValues(): void {
    if (
      this.selectedDestination &&
      this.testMode == TestMode.LOOPBACK &&
      this.selectedDestination.deviceData?.type !== 'unmanaged'
    ) {
      this.onDataEmit(ConfigFieldMapping.SELECTED_DESTINATION, this.selectedDestination, true);
      if (this.selectedDestination.getModel()?.includes('JMEP')) {
        this.isJmepAsLb = true;
        this.formField.destEquipmentMac =
          this.selectedDestination.deviceData.capabilities?.phy['equipment-mac'];
        this.formField.destNetworkMac =
          this.selectedDestination.deviceData.capabilities?.phy['network-mac'];
        this.ipFields.destAssignedAddress =
          this.selectedDestination.deviceData.capabilities?.phy['assigned-ip'];
        this.ipFields.destTestIpAddress =
          this.selectedDestination.deviceData.capabilities?.phy['test-ip'];

        if (this.badIpValues.includes(this.ipFields.destTestIpAddress)) {
          const index = findIndex(this.destDefinedAddressTypes, {
            value: definedAddressValue.TEST_IP
          });
          if (index !== -1) {
            this.destDefinedAddressTypes[index].enabled = false;
            if (!this.badIpValues.includes(this.ipFields.destAssignedAddress)) {
              this.formField.destDefinedAddress = definedAddressValue.MANAGEMENT_IP;
            } else {
              this.formField.destDefinedAddress = definedAddressValue.MANUAL;
            }
          }
        }
        if (this.badIpValues.includes(this.ipFields.destAssignedAddress)) {
          const index = findIndex(this.destDefinedAddressTypes, {
            value: definedAddressValue.MANAGEMENT_IP
          });
          if (index !== -1) {
            this.destDefinedAddressTypes[index].enabled = false;
            if (!this.badIpValues.includes(this.ipFields.destTestIpAddress)) {
              this.formField.destDefinedAddress = definedAddressValue.TEST_IP;
            } else {
              this.formField.destDefinedAddress = definedAddressValue.MANUAL;
            }
          }
        }
        if (!this.newTestMode) {
          if (this.formField.destIpAddress === this.ipFields.destTestIpAddress) {
            this.formField.destDefinedAddress = definedAddressValue.TEST_IP;
          } else if (this.formField.destIpAddress === this.ipFields.destAssignedAddress) {
            this.formField.destDefinedAddress = definedAddressValue.MANAGEMENT_IP;
          } else {
            this.ipFields.destManualAddress = this.formField.destIpAddress;
            this.formField.destDefinedAddress = definedAddressValue.MANUAL;
          }
          if (
            this.formField.destMacAddress.indexOf('-') === -1 &&
            this.formField.destEquipmentMac.indexOf('-') !== -1
          ) {
            this.formField.destMacAddress = this.formField.destMacAddress.replaceAll(':', '-');
          }
          if (this.formField.destMacAddress === this.formField.destEquipmentMac) {
            this.formField.destTxInterface = TRANSMIT_INTERFACE_VALUE.EQUIPMENT;
          } else {
            this.formField.destTxInterface = TRANSMIT_INTERFACE_VALUE.NETWORK;
          }
        }
        this.onDestDefinedAddressChange();
        this.onDestTxInterfaceChange();
      } else {
        this.isJmepAsLb = false;
      }
      this.onDestinationMacAddressChange();
      this.onDestinationIpAddressChange();
    }
  }

  setUnManagedDeviceValues(deviceId: string): void {
    this.selectedDestination = this.unmanagedDevicesService.findDeviceByID(deviceId);
    this.showDestinationDetails = true;

    if (this.selectedDestination) {
      this.onDataEmit(ConfigFieldMapping.SELECTED_DESTINATION, this.selectedDestination, true);
      this.formField.destMacAddress =
        this.selectedDestination.deviceData.capabilities?.['l2-loopback']?.['mac'] ??
        this.formField.destMacAddress;
      this.formField.destIpAddress =
        this.selectedDestination.deviceData.capabilities?.['l3-ipv4-loopback']?.['ip'] ??
        this.formField.destIpAddress;
      this.onDestinationMacAddressChange();
      this.onDestinationIpAddressChange();
    }
  }

  getDataBasedOnAgentConfig(): void {
    if (this.selectedOriginator) {
      this.showOrginatorDetails = true;
      this.showDestinationDetails = this.testMode == TestMode.LOOPBACK;
      this.setupService.setOriginatorAgent(this.selectedOriginator);
      const capabilityData = this.setupService.getAgentLookUpData(
        this.config.lookup,
        this.selectedOriginator
      );
      this.ipFields.srcAssignedAddress =
        capabilityData[ConfigFieldMapping.SRC_ASSIGNED_IP_ADDRESS] ??
        this.ipFields.srcAssignedAddress;
      this.ipFields.srcTestIpAddress =
        capabilityData[ConfigFieldMapping.SRC_TEST_IP_ADDRESS] ?? this.ipFields.srcTestIpAddress;
      this.ipFields.srcSubnet = capabilityData[ConfigFieldMapping.SRC_SUBNET]
        ? this.findSubnetMapValue(capabilityData[ConfigFieldMapping.SRC_SUBNET])
        : this.formField.srcSubnet;
      this.ipFields.srcGateway =
        capabilityData[ConfigFieldMapping.SRC_GATEWAY] ?? this.formField.srcGateway;
      this.formField.srcMacAddress =
        capabilityData[ConfigFieldMapping.SRC_MAC_ADDRESS] ?? this.formField.srcMacAddress;
      this.formField.srcNetworkMac = capabilityData[ConfigFieldMapping.SRC_NETWORK_MAC];
      this.formField.srcEquipmentMac = capabilityData[ConfigFieldMapping.SRC_EQUIPMENT_MAC];
      this.onSourceIpAddressChange();
      this.onSourceGatewayChange();
      this.onSourceMacAddressChange();
      this.onSourceSubnetChange();
      this.onSourceEquipmentMacAddressChange();
      this.onSourceNetworkMacAddressChange();
      this.onSourceTestIPAddressChange();
      this.onSourceAssignedIPAddressChange();
    }
    if (this.selectedDestination) {
      this.setupService.setDestinationAgent(this.selectedDestination);
      this.showDestinationDetails = true;
      if (this.onAgentChanged) {
        const capabilityData = this.setupService.getAgentLookUpData(
          this.config.lookup,
          this.selectedDestination
        );
        this.formField.destIpV4Address =
          capabilityData[ConfigFieldMapping.DEST_IPV4_ADDRESS] ?? this.formField.destIpV4Address;
        this.formField.destMacAddress =
          capabilityData[ConfigFieldMapping.DESTINATION_MAC] ?? this.formField.destMacAddress;
        this.onDestinationIpAddressChange();
        this.onDestinationMacAddressChange();
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  findSubnetMapValue(subnet: string): string {
    subnet = subnet.toString();
    if (subnet && !subnet.includes('.')) {
      return SUBNET_MAP[subnet] ?? subnet;
    }
    return subnet;
  }

  onTxInterfaceChange(): void {
    this.txTypeImage =
      this.formField.txInterface === TRANSMIT_INTERFACE_VALUE.EQUIPMENT
        ? TX_IMAGE.EQUIPMENT
        : TX_IMAGE.NETWORK;
    this.formField.srcMacAddress =
      this.formField.txInterface === TRANSMIT_INTERFACE_VALUE.EQUIPMENT
        ? this.formField.srcEquipmentMac
        : this.formField.srcNetworkMac;
    this.onSourceMacAddressChange();
    this.onDataEmit(ConfigFieldMapping.TRANSMIT_INTERFACE, this.formField.txInterface, true);
  }

  onDefinedAddressChange(): void {
    this.formField.srcEditLayer2IP = false;
    this.formField.srcEditLayer3IP = false;
    this.formField.srcEditLayer3Subnet = true;
    if (this.formField.definedAddress === definedAddressValue.TEST_IP) {
      this.formField.srcIpAddress = this.ipFields.srcTestIpAddress;
      this.formTooltip.srcIpAddress = 'Test IP address that is assigned to JMEP';
      this.formField.srcEditLayer3Subnet = false;
    } else if (this.formField.definedAddress === definedAddressValue.MANAGEMENT_IP) {
      this.formField.srcIpAddress = this.ipFields.srcAssignedAddress;
      this.formTooltip.srcIpAddress = 'Management IP address that is assigned to JMEP';
    } else {
      this.formField.srcEditLayer2IP = true;
      this.formField.srcEditLayer3IP = true;
      this.formTooltip.srcIpAddress = '';
    }
    this.onDataEmit(ConfigFieldMapping.DEFINED_ADDRESS, this.formField.definedAddress, true);
    this.onSourceIpAddressChange();
  }

  onSourceAddressFromChange(): void {
    this.onDataEmit(ConfigFieldMapping.SOURCE_ADDRESS_FROM, this.formField.sourceAddressFrom, true);
    if (this.formField.sourceAddressFrom === sourceAddressFrom.DHCP) {
      this.onDataEmit(ConfigFieldMapping.SRC_IPV4_ADDRESS, this.formField.sourceAddressFrom, true);
      this.dhcpEnabled = true;
    } else {
      this.formField.srcIpAddress =
        this.formField.srcIpAddress === sourceAddressFrom.DHCP
          ? this.ipFields.srcTestIpAddress
          : this.formField.srcIpAddress;
      this.onDataEmit(ConfigFieldMapping.SRC_IPV4_ADDRESS, this.formField.srcIpAddress, true);
      this.dhcpEnabled = false;
    }
  }

  onSourceNetworkMacAddressChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_NETWORK_MAC, this.formField.srcNetworkMac, true);
  }

  onSourceEquipmentMacAddressChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_EQUIPMENT_MAC, this.formField.srcEquipmentMac, true);
  }

  onSourceTestIPAddressChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_TEST_IP_ADDRESS, this.ipFields.srcTestIpAddress, true);
  }

  onSourceAssignedIPAddressChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.SRC_ASSIGNED_IP_ADDRESS,
      this.ipFields.srcAssignedAddress,
      true
    );
  }

  @Debounce(500)
  onSourceMacAddressChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_MAC_ADDRESS, this.formField.srcMacAddress, true);
  }

  @Debounce(500)
  onSourceIpAddressChange(): void {
    if (this.ipType === IPAddressValue.IP_V4) {
      this.onDataEmit(ConfigFieldMapping.SRC_IPV4_ADDRESS, this.formField.srcIpAddress, true);
    } else {
      this.onDataEmit(ConfigFieldMapping.SRC_IPV6_ADDRESS, this.formField.srcIpAddress, true);
    }
  }

  @Debounce(500)
  onSourceSubnetChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_SUBNET, this.formField.srcSubnet, true);
  }

  @Debounce(500)
  onSourceGatewayChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_GATEWAY, this.formField.srcGateway, true);
  }

  onArpChange(): void {
    this.formField.destShowMacLayer3 = !this.formField.useArp;
    this.onDataEmit(ConfigFieldMapping.USE_ARP, this.formField.useArp, true);
    this.onDataEmit(
      ConfigFieldMapping.DEST_SHOW_MAC_LAYER3,
      this.formField.destShowMacLayer3,
      true
    );
  }

  onDestTxInterfaceChange(): void {
    this.destTxTypeImage =
      this.formField.destTxInterface === TRANSMIT_INTERFACE_VALUE.EQUIPMENT
        ? TX_IMAGE.EQUIPMENT
        : TX_IMAGE.NETWORK;
    this.formField.destMacAddress =
      this.formField.destTxInterface === TRANSMIT_INTERFACE_VALUE.EQUIPMENT
        ? this.formField.destEquipmentMac
        : this.formField.destNetworkMac;
    this.onDestinationMacAddressChange();
    this.onDataEmit(
      ConfigFieldMapping.DEST_TRANSMIT_INTERFACE,
      this.formField.destTxInterface,
      true
    );
  }

  onDestDefinedAddressChange(): void {
    if (this.formField.destDefinedAddress === definedAddressValue.TEST_IP) {
      this.formField.destIpAddress = this.ipFields.destTestIpAddress;
    } else if (this.formField.destDefinedAddress === definedAddressValue.MANAGEMENT_IP) {
      this.formField.destIpAddress = this.ipFields.destAssignedAddress;
    } else {
      this.formField.destIpAddress =
        this.ipFields.destManualAddress || this.ipFields.destAssignedAddress;
    }
    this.onDataEmit(
      ConfigFieldMapping.DEST_DEFINED_ADDRESS,
      this.formField.destDefinedAddress,
      true
    );
    this.onDestinationIpAddressChange();
  }

  onDestinationMacAddressChange(): void {
    this.onDataEmit(ConfigFieldMapping.DESTINATION_MAC, this.formField.destMacAddress, true);
  }

  onDestinationIpAddressChange(): void {
    this.onDataEmit(ConfigFieldMapping.DEST_IPV4_ADDRESS, this.formField.destIpAddress, true);
  }

  // eslint-disable-next-line
  setManualDestAddress(event): void {
    event.preventDefault();
    this.showDestinationDetails = true;
  }

  onVlanConfigChange(): void {
    this.onDataEmit(ConfigFieldMapping.VLAN_CONFIG, this.formField.vlanConfig, true);
    this.updateEtherTypeOptions();
    if (this.formField.vlanConfig === vlanConfigValue.SINGLE_VLAN) {
      this.onSVlanChange();
      this.onDataEmit(ConfigFieldMapping.CVLAN_ID, null, true);
    } else if (this.formField.vlanConfig === vlanConfigValue.Q_IN_Q) {
      this.onSVlanChange();
      this.onCVlanChange();
    } else {
      this.onDataEmit(ConfigFieldMapping.SVLAN_ID, null, true);
      this.onDataEmit(ConfigFieldMapping.CVLAN_ID, null, true);
    }
  }

  onSVlanChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.SVLAN_ID,
      this.formField.svlan,
      this.form.controls['svlan']?.valid
    );
  }

  updateEtherTypeOptions(): void {
    if (this.formField.vlanConfig) {
      this.etherTypesData = SVLAN_ETHER_TYPE.filter(
        type => type.vlanType === this.formField.vlanConfig
      );
      if (this.etherTypesData?.length > 0) {
        this.formField.etherType = this.etherTypesData[0].value;
      }
    }
    this.onEtherTypeChange();
  }

  onEtherTypeChange(): void {
    this.onDataEmit(ConfigFieldMapping.ETHER_TYPE, this.formField.etherType, true);
  }

  onCVlanChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.CVLAN_ID,
      this.formField.cvlan,
      this.form.controls['cvlan']?.valid
    );
  }

  onCVlanEtherTypeChange(): void {
    this.onDataEmit(ConfigFieldMapping.CVLAN_ETHER_TYPE, this.formField.cVlanEtherType, true);
  }

  onDeiEnableChange(): void {
    this.onDataEmit(ConfigFieldMapping.DEI_ENABLE, this.formField.deiEnable, true);
  }

  onVlanPriorityChange(): void {
    this.onDataEmit(ConfigFieldMapping.VERIFY_PRIORITY, this.formField.vlanPriority, true);
  }

  onSuppressLoopChange(): void {
    if (!this.formField.suppressLoop) {
      this.formField.useArp = true;
      this.onArpChange();
    }
    this.onDataEmit(ConfigFieldMapping.SUPPRESS_LOOP_COMMANDS, this.formField.suppressLoop, true);
    this.selecteUnmanagedLoopbackTypes();
  }

  selecteUnmanagedLoopbackTypes(): void {
    this.selectedLoopbackTypes =
      this.layer === 'L2' ? LoopbackTypes.L2_LOOPBACK : LoopbackTypes.L3_IPV4_LOOPBACK;
  }

  onSwapMacChange(): void {
    this.onDataEmit(ConfigFieldMapping.SWAP_MAC_ADDRESS, this.formField.swapMac, true);
  }

  checkLayerType(): void {
    this.layer3Enabled = this.layer === 'L3' || this.layer === 'L4';
    this.layer2Enabled = this.layer === 'L2';
  }
}
